import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { ToastProvider } from './contexts/ToastContext';
import ScrollToTop from './components/ScrollToTop';
import Toaster from './components/Toaster';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import TenantSettings from './pages/settings/TenantSettings';
import UserSettings from './pages/settings/UserSettings';
import RolesSettings from './pages/settings/RolesSettings';
import MovementTypesSettings from './pages/settings/MovementTypesSettings';
import SuppliesList from './pages/production/SuppliesList';
import SupplyForm from './pages/production/SupplyForm';
import ResourcesList from './pages/production/ResourcesList';
import ResourceForm from './pages/production/ResourceForm';
import ProductsList from './pages/production/ProductsList';
import ProductForm from './pages/production/ProductForm';
import ProductLinesList from './pages/production/ProductLinesList';
import ProductionOrdersList from './pages/production/ProductionOrdersList';
import ProductionOrderDetail from './pages/production/ProductionOrderDetail';
import WarehousesList from './pages/production/WarehousesList';
import ClientsList from './pages/clients/ClientsList';
import ClientTypesList from './pages/clients/ClientTypesList';
import SalesList from './pages/sales/SalesList';
import SaleForm from './pages/sales/SaleForm';
import MinMaxRestockReport from './pages/reports/MinMaxRestockReport';
import ProjectedStockReport from './pages/reports/ProjectedStockReport';
import AnnualStockReport from './pages/reports/AnnualStockReport';
import StockMovementsReport from './pages/reports/StockMovementsReport';
import ProductionOrderReportsReport from './pages/reports/ProductionOrderReportsReport';
import SaleFulfillmentReport from './pages/reports/SaleFulfillmentReport';
import SaleBacklogReport from './pages/reports/SaleBacklogReport';

function PrivateRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) return null;

    return user ? children : <Navigate to="/login" />;
}

function ModuleRoute({ module, children }) {
    const { user, loading, can } = useAuth();

    if (loading) return null;

    if (!user) return <Navigate to="/login" />;
    if (!can(module)) return <Navigate to="/dashboard" />;

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected routes */}
            <Route
                path="/dashboard"
                element={
                    <PrivateRoute>
                        <Dashboard />
                    </PrivateRoute>
                }
            />

            {/* Production */}
            <Route
                path="/production/supplies"
                element={
                    <ModuleRoute module="Supplies">
                        <SuppliesList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/supplies/new"
                element={
                    <ModuleRoute module="Supplies">
                        <SupplyForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/supplies/:id/edit"
                element={
                    <ModuleRoute module="Supplies">
                        <SupplyForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/resources"
                element={
                    <ModuleRoute module="Resources">
                        <ResourcesList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/resources/new"
                element={
                    <ModuleRoute module="Resources">
                        <ResourceForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/resources/:id/edit"
                element={
                    <ModuleRoute module="Resources">
                        <ResourceForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/lines/:id?"
                element={
                    <ModuleRoute module="Products">
                        <ProductLinesList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/products"
                element={
                    <ModuleRoute module="Products">
                        <ProductsList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/products/new"
                element={
                    <ModuleRoute module="Products">
                        <ProductForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/products/:id/edit"
                element={
                    <ModuleRoute module="Products">
                        <ProductForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/warehouses"
                element={<Navigate to="/settings/warehouses" />}
            />
            <Route
                path="/production/orders"
                element={
                    <ModuleRoute module="Orders">
                        <ProductionOrdersList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/orders/new"
                element={
                    <ModuleRoute module="Orders">
                        <ProductionOrderDetail />
                    </ModuleRoute>
                }
            />
            <Route
                path="/production/orders/:id"
                element={
                    <ModuleRoute module="Orders">
                        <ProductionOrderDetail />
                    </ModuleRoute>
                }
            />

            {/* Relatórios */}
            <Route
                path="/reports/min-max-restock"
                element={
                    <ModuleRoute module="Reports">
                        <MinMaxRestockReport />
                    </ModuleRoute>
                }
            />
            <Route
                path="/reports/projected-stock"
                element={
                    <ModuleRoute module="Reports">
                        <ProjectedStockReport />
                    </ModuleRoute>
                }
            />
            <Route
                path="/reports/annual-stock"
                element={
                    <ModuleRoute module="Reports">
                        <AnnualStockReport />
                    </ModuleRoute>
                }
            />
            <Route
                path="/reports/stock-movements"
                element={
                    <ModuleRoute module="Reports">
                        <StockMovementsReport />
                    </ModuleRoute>
                }
            />

            <Route
                path="/reports/production-order-reports"
                element={
                    <ModuleRoute module="Reports">
                        <ProductionOrderReportsReport />
                    </ModuleRoute>
                }
            />

            <Route
                path="/reports/sale-fulfillment"
                element={
                    <ModuleRoute module="Reports">
                        <SaleFulfillmentReport />
                    </ModuleRoute>
                }
            />

            <Route
                path="/reports/sale-backlog"
                element={
                    <ModuleRoute module="Reports">
                        <SaleBacklogReport />
                    </ModuleRoute>
                }
            />

            {/* Settings */}
            <Route
                path="/settings/tenant"
                element={
                    <ModuleRoute module="Tenant">
                        <TenantSettings />
                    </ModuleRoute>
                }
            />
            <Route
                path="/settings/user/:id?"
                element={
                    <ModuleRoute module="User">
                        <UserSettings />
                    </ModuleRoute>
                }
            />
            <Route
                path="/settings/roles/:id?"
                element={
                    <ModuleRoute module="Roles">
                        <RolesSettings />
                    </ModuleRoute>
                }
            />
            <Route
                path="/settings/warehouses/:id?"
                element={
                    <ModuleRoute module="Warehouses">
                        <WarehousesList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/settings/movement-types/:id?"
                element={
                    <ModuleRoute module="MovementTypes">
                        <MovementTypesSettings />
                    </ModuleRoute>
                }
            />
            <Route
                path="/settings/clients/:id?"
                element={
                    <ModuleRoute module="Clients">
                        <ClientsList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/settings/client-types/:id?"
                element={
                    <ModuleRoute module="Clients">
                        <ClientTypesList />
                    </ModuleRoute>
                }
            />

            {/* Vendas (Pedidos) */}
            <Route
                path="/sales"
                element={
                    <ModuleRoute module="Sales">
                        <SalesList />
                    </ModuleRoute>
                }
            />
            <Route
                path="/sales/new"
                element={
                    <ModuleRoute module="Sales">
                        <SaleForm />
                    </ModuleRoute>
                }
            />
            <Route
                path="/sales/:id"
                element={
                    <ModuleRoute module="Sales">
                        <SaleForm />
                    </ModuleRoute>
                }
            />

            {/* Default route */}
            <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
    );
}

function App() {
    return (
        <Router>
            <ScrollToTop />
            <ToastProvider>
                <LoadingProvider>
                    <AuthProvider>
                        <AppRoutes />
                        <Toaster />
                    </AuthProvider>
                </LoadingProvider>
            </ToastProvider>
        </Router>
    );
}

export default App;

